/**
 * String formatting utilities
 */

/**
 * Sanitizes a name by trimming, removing extra spaces, and capitalizing each word
 * @param name - The name string to sanitize
 * @returns Properly formatted name
 * @example
 * sanitizeName("JOHN DOE") // "John Doe"
 * sanitizeName("mary jane") // "Mary Jane"
 */
export function sanitizeName(name: string): string {
  return name
    .trim()
    .replace(/\s+/g, ' ')
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Capitalizes the first letter of a string
 * @param str - The string to capitalize
 * @returns String with first letter capitalized
 * @example
 * capitalize("hello world") // "Hello world"
 */
export function capitalize(str: string): string {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

/**
 * Converts a string to title case (each word capitalized)
 * @param str - The string to convert
 * @returns String in title case
 * @example
 * titleCase("hello world") // "Hello World"
 */
export function titleCase(str: string, separator = ' '): string {
  return str
    .toLowerCase()
    .split(separator)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(separator);
}

/**
 * Normalizes whitespace in a string (trims and removes extra spaces)
 * @param str - The string to normalize
 * @returns String with normalized whitespace
 * @example
 * normalizeSpaces("  hello    world  ") // "hello world"
 */
export function normalizeSpaces(str: string): string {
  return str.trim().replace(/\s+/g, ' ');
}

/**
 * Truncates a string to a specified length and adds ellipsis
 * @param str - The string to truncate
 * @param maxLength - Maximum length before truncation
 * @param suffix - Suffix to add when truncated (default: "...")
 * @returns Truncated string
 * @example
 * truncate("This is a long string", 10) // "This is a..."
 */
export function truncate(
  str: string,
  maxLength: number,
  suffix = '...',
): string {
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength - suffix.length) + suffix;
}
